import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
} from '@reduxjs/toolkit';
import { rejectValue, flatRefund } from 'redux/helpers';
import format from 'date-fns/format';

import { GET_REFUNDS, POST_EXPORT_REFUNDS } from 'services/api';
import { REFUND, REFUND_ALL, REFUND_SEARCH, REFUND_EXPORT } from './types';
import { initialRange } from 'components/DateRangePicker/ranges';
import { DATE_FOR_API } from 'utils/dateFormat';
import { AMOUNT_FILTERS } from 'components/PaymentFilter/defaultFilters';
import { priceForAPI } from 'utils/price';
import {
  STATUS_FAILED,
  STATUS_PENDING,
  STATUS_PROCESSED,
  STATUS_REVERSED,
  REASON_DUPLICATE,
  REASON_FRAUDULENT,
  REASON_NOT_GIVEN,
  REASON_OTHER,
  REASON_REQUESTED_BY_CUSTOMER
} from 'pages/Refunds/helpers';
import { showSuccessMessage } from '../notification';

const refundAdapter = createEntityAdapter();

export const initialState = refundAdapter.getInitialState({
  isLoading: false,
  detailsIsLoading: false,
  pagination: {
    total: 1,
    total_pages: 1,
    current_page: 1,
    per_page: 25,
    count: 1,
  },
  params: {
    per_page: 25,
    from: format(initialRange.startDate, DATE_FOR_API),
    to: format(initialRange.endDate, DATE_FOR_API),
    amount_filter: AMOUNT_FILTERS[1],
    amount: null,
    statusCheckbox: true,
    reasonCheckbox: true,
    status: `${STATUS_PROCESSED},${STATUS_REVERSED},${STATUS_PENDING},${STATUS_FAILED}`,
    reason: `${REASON_REQUESTED_BY_CUSTOMER},${REASON_DUPLICATE},${REASON_FRAUDULENT},${REASON_OTHER},${REASON_NOT_GIVEN}`
  },
  search: {
    term: '',
    filter: undefined,
    submitted: false,
  },
});

export const getRefunds = createAsyncThunk(
  REFUND_ALL,
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      const companyId = getState().company.current;
      const { status, amount, statusCheckbox, amount_filter, reason, reasonCheckbox, ..._params } =
        getState().refund.params;

      let params = { ..._params };

      if (statusCheckbox && status) {
        params = { ...params, status };
      }

      if (reasonCheckbox && reason) {
        params = { ...params, reason }
      }

      if (!!amount) {
        const amountForAPI = priceForAPI(amount);

        if (amount_filter.value === AMOUNT_FILTERS[0].value) {
          params = { ...params, amount_over: amountForAPI };
        } else if (amount_filter.value === AMOUNT_FILTERS[2].value) {
          params = { ...params, amount_under: amountForAPI };
        } else {
          params = { ...params, amount_equal_to: amountForAPI };
        }
      }
      const { data, meta } = await GET_REFUNDS({
        ...params,
        company_id: companyId,
        include: 'transaction',
      });

      if (!data.length) return { data: [], pagination: meta.pagination };
      const _data = data.map(flatRefund);

      return { data: _data, pagination: meta.pagination };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);
export const searchRefunds = createAsyncThunk(
  REFUND_SEARCH,
  async (_, { rejectWithValue, getState }) => {
    try {
      const companyId = getState().company.current;
      const { term } = getState().refund.search;

      if (!term) {
        return;
      }

      const { data, meta } = await GET_REFUNDS({
        search: term,
        company_id: companyId,
      });

      if (!data.length) return { data: [], pagination: meta.pagination };

      const _data = data.map(flatRefund);

      return { data: _data, pagination: meta.pagination };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const exportRefunds = createAsyncThunk(
  REFUND_EXPORT,
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      const email = getState().auth.user.email;
      const { term, filter, submitted } = getState().refund.search;

      let params = {};

      if (term && filter && submitted) {
        params = { [filter.value]: term };
      } else {
        const {
          status,
          statusCheckbox,
          amount,
          amount_filter,
          per_page,
          page,
          ..._params
        } = getState().refund.params;

        params = { ..._params };
        if (statusCheckbox && status) {
          params = { ...params, status };
        }
        if (!!amount) {
          const amountForAPI = priceForAPI(amount);
          params = { ...params, [amount_filter.value]: amountForAPI };
        }
      }
      await POST_EXPORT_REFUNDS(params);
      dispatch(
        showSuccessMessage(
          `Check your email account ${email} for access to the file.`
        )
      );
    } catch (error) {
      return rejectWithValue(
        rejectValue({ ...error.response, skipNotification: true })
      );
    }
  }
);

const refundSlice = createSlice({
  name: REFUND,
  initialState,
  reducers: {
    setParams: (state, { payload }) => {
      state.params = { ...state.params, ...payload };
    },
    setSearchTerm: (state, { payload }) => {
      state.search.term = payload;
    },
    resetSearch: (state) => {
      state.search = initialState.search;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getRefunds.pending, (state) => {
        state.isLoading = true;
        state.search.submitted = false;
      })
      .addCase(getRefunds.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getRefunds.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        refundAdapter.setAll(state, payload.data);
        state.pagination = payload.pagination;
      })
      .addCase(searchRefunds.pending, (state) => {
        state.isLoading = true;
        state.search.submitted = true;
      })
      .addCase(searchRefunds.rejected, (state) => {
        state.isLoading = false;
        state.search.submitted = false;
      })
      .addCase(searchRefunds.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        refundAdapter.setAll(state, payload.data);
        state.pagination = payload.pagination;
      })
      .addDefaultCase((state) => state);
  },
});

export const refundSelectors = refundAdapter.getSelectors(
  (state) => state.refund
);

export const refundByIdSelector = (state, id) => ({
  data: refundSelectors.selectById(state, id),
  isLoading: state.refund.detailsIsLoading,
});

export const refundAllSelector = (state) => ({
  data: refundSelectors.selectAll(state),
  pagination: state.refund.pagination,
  isLoading: state.refund.isLoading,
  params: state.refund.params,
  searchTerm: state.refund.search.term
});
export const { setParams, setSearchTerm, resetSearch } = refundSlice.actions;

export default refundSlice.reducer;
